/**
 * File: KnowledgeBasePage.tsx
 * Description: Lists every document the agents can retrieve from the shared
 *   knowledge base, and lets users drag-and-drop new documents that will be
 *   auto-ingested via the AI Search indexer. When rendered in "selection"
 *   mode (used by the welcome flow) the page also lets users pre-select the
 *   documents they want to start every new project with.
 * Author: GitHub Copilot
 * Created: 2026-05-31
 * Last Modified: 2026-05-31
 */
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import headerBg from '../../assets/header-bg.svg';
import logo from '../../assets/logo.svg';
import { FileUploader, UploadedFile } from '../../components/FileUploader/FileUploader';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  GENERAL_LINE_OF_BUSINESS,
  KnowledgeBaseDocument,
  KnowledgeBaseService,
} from '../../services/knowledgeBaseService';
import { humanizeDescription } from '../../utils/humanizeDescription';
import {
  getPreselectedKnowledgeBaseDocuments,
  loadPreselectedKnowledgeBaseDocuments,
  savePreselectedKnowledgeBaseDocuments,
} from '../../utils/preselectedKnowledgeBase';

import '../LineOfBusinessWelcome/LineOfBusinessWelcomePage.css';
import './KnowledgeBaseWelcome.css';

const PAGE_TITLE = 'Knowledge Base';
const PAGE_DESCRIPTION =
  'Every document the Archie agents can retrieve when answering you. ' +
  'Drag and drop new files to add them to the shared knowledge base.';
const SELECTION_DESCRIPTION =
  'Pick the guidelines you want pre-selected for every new project. ' +
  'You can change this later from the user menu.';
const EMPTY_STATE = 'No documents found in the knowledge base yet.';
const UPLOAD_LABEL = 'Add documents to the knowledge base';
const UPLOAD_DESCRIPTION =
  'Drag and drop files here, or click to browse. Once you have added your ' +
  'files, click "Upload to knowledge base" to index them and make them ' +
  'searchable.';

const SEARCH_PLACEHOLDER = 'Search by name, keyword, or description...';
const CONTINUE_LABEL = 'Confirm and Continue';
const SKIP_LABEL = 'Skip for now';
const BACK_LABEL = 'Back';
const UPLOAD_INDEX_LABEL = 'Upload to knowledge base';
const INDEXING_LABEL = 'Indexing…';
const SELECTED_SECTION_TITLE = 'Your Selected Guidelines';
const SELECTED_SECTION_EMPTY =
  'No guidelines selected yet. Tick documents below to add them.';
// Two density variants of the same section: the standalone page has room to
// breathe, the welcome step must stay compact so the list below keeps its
// height. The welcome list height itself is capped in CSS.
const SELECTED_STYLES = {
  page: {
    heading: 'flex items-center gap-2 text-lg font-semibold text-gray-900',
    headingIcon: 'h-5 w-5 text-green-600',
    badge:
      'rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700',
    empty: 'mt-3 text-sm text-gray-500',
    list: 'mt-3 max-h-64 divide-y divide-gray-100 overflow-y-auto pr-1',
    row: 'flex items-center gap-3 py-2',
    rowIcon: 'h-5 w-5 flex-shrink-0 text-gray-400',
    name: 'min-w-0 flex-1 truncate text-sm font-medium text-gray-900',
    remove:
      'flex-shrink-0 rounded border border-gray-300 px-2 py-1 text-xs ' +
      'font-medium text-gray-600 hover:bg-gray-50',
  },
  welcome: {
    heading: 'flex items-center gap-2 text-sm font-semibold text-gray-900',
    headingIcon: 'h-4 w-4 text-green-600',
    badge:
      'rounded-full bg-green-100 px-1.5 py-0.5 text-[0.65rem] font-medium ' +
      'text-green-700',
    empty: 'mt-2 text-xs text-gray-500',
    list: 'knowledge-base-welcome__selectedList mt-2 divide-y ' +
      'divide-gray-100 pr-1',
    row: 'flex items-center gap-2 py-1',
    rowIcon: 'h-4 w-4 flex-shrink-0 text-gray-400',
    name: 'min-w-0 flex-1 truncate text-xs font-medium text-gray-900',
    remove:
      'flex-shrink-0 rounded border border-gray-300 px-1.5 py-0.5 ' +
      'text-[0.7rem] font-medium text-gray-600 hover:bg-gray-50',
  },
} as const;

type SelectedVariant = keyof typeof SELECTED_STYLES;
// Poll the indexer status every few seconds while a run is in progress, up to
// a safety cap so the button can never get stuck disabled forever.
const INDEXER_POLL_INTERVAL_MS = 4000;
const INDEXER_POLL_MAX_ATTEMPTS = 75;
const INDEXER_STATUS_RUNNING = 'running';
// "All" pseudo-option for the Available Guidelines line-of-business filter.
const LOB_FILTER_ALL = 'All';
const UPLOAD_LOB_LABEL = 'Line of business for these documents';
// Placeholder forcing a deliberate choice before uploading (no silent default).
const LOB_UNSELECTED = '';
const LOB_PLACEHOLDER_LABEL = 'Select a line of business…';
const LOB_REQUIRED_MESSAGE =
  'Please select a line of business before uploading.';
// Branding shown in the welcome-flow hero header, matching the first
// onboarding step (LineOfBusinessWelcomePage) so the flow feels consistent.
const HERO_TITLE = 'AI Assisted Architecture';

interface KnowledgeBasePageProps {
  /** When true the page shows multi-select + a "Confirm and Continue" CTA. */
  selectionMode?: boolean;
  /** Called after the user confirms their selection in selection mode. */
  onConfirm?: (selected: KnowledgeBaseDocument[]) => void;
  /** Called when the user wants to go back to the previous welcome step. */
  onBack?: () => void;
}

const matchesQuery = (doc: KnowledgeBaseDocument, query: string): boolean => {
  if (!query) {
    return true;
  }
  const haystack = [
    doc.name,
    doc.description ?? '',
    doc.sector ?? '',
    ...(doc.keywords ?? []),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const KnowledgeBasePage: React.FC<KnowledgeBasePageProps> = ({
  selectionMode = false,
  onConfirm,
  onBack,
}) => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState<boolean>(false);
  const [hasPendingUploads, setHasPendingUploads] = useState<boolean>(false);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>(
    [GENERAL_LINE_OF_BUSINESS],
  );
  const [uploadLineOfBusiness, setUploadLineOfBusiness] = useState<string>(
    LOB_UNSELECTED,
  );
  const [lobFilters, setLobFilters] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    // Seed from the saved preselection in both modes so the standalone
    // knowledge base page reflects the same project-context selection the
    // user made during the welcome flow.
    () => new Set(getPreselectedKnowledgeBaseDocuments().map((doc) => doc.id)),
  );

  const loadDocuments = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const fetched = await KnowledgeBaseService.listDocuments();
      setDocuments(fetched);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load knowledge base documents',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  // Load the line-of-business choices (profile options + General) used by the
  // upload selector and the Available Guidelines filter.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const options = await KnowledgeBaseService.getLineOfBusinessOptions();
        if (!cancelled && options.length > 0) {
          setLineOfBusinessOptions(options);
        }
      } catch {
        // Keep the default [General] when options can't be loaded.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the persisted selection from the user's backend profile so the
  // standalone page mirrors what was chosen in the welcome flow (on any
  // device). Falls back to the local cache when the backend is unavailable.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const documents = await loadPreselectedKnowledgeBaseDocuments();
      if (!cancelled) {
        setSelectedIds(new Set(documents.map((doc) => doc.id)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc) => {
        if (!matchesQuery(doc, query)) {
          return false;
        }
        if (lobFilters.size === 0) {
          return true;
        }
        const docLob = doc.line_of_business ?? GENERAL_LINE_OF_BUSINESS;
        return lobFilters.has(docLob);
      }),
    [documents, query, lobFilters],
  );

  // Documents the user has selected, shown in the "Your Selected Guidelines"
  // section above the full list.
  const selectedDocuments = useMemo(
    () => documents.filter((doc) => selectedIds.has(doc.id)),
    [documents, selectedIds],
  );

  // Filter chips: All, then General, then the remaining lines of business.
  const lineOfBusinessFilterOptions = useMemo(() => {
    const rest = lineOfBusinessOptions.filter(
      (option) => option !== GENERAL_LINE_OF_BUSINESS,
    );
    return [LOB_FILTER_ALL, GENERAL_LINE_OF_BUSINESS, ...rest];
  }, [lineOfBusinessOptions]);

  const handleFilesUploaded = useCallback(
    async (files: UploadedFile[]): Promise<void> => {
      setUploadError(null);
      setUploadStatus(null);
      // A line of business must be chosen deliberately before uploading.
      if (!uploadLineOfBusiness) {
        setUploadError(LOB_REQUIRED_MESSAGE);
        return;
      }
      setUploadedFiles((prev) => [...prev, ...files]);
      const statusMessages: string[] = [];
      await Promise.all(
        files.map(async (uploadedFile) => {
          try {
            const result = await KnowledgeBaseService.uploadDocument(
              uploadedFile.file,
              uploadLineOfBusiness,
            );
            if (result.duplicate) {
              // The document already exists; reject it in the UI rather than
              // showing it as a successful upload.
              setUploadError(result.message);
              setUploadedFiles((prev) =>
                prev.map((existing) =>
                  existing.id === uploadedFile.id
                    ? {
                      ...existing,
                      status: 'error',
                      error: result.message,
                    }
                    : existing,
                ),
              );
              return;
            }
            if (result.message) {
              statusMessages.push(result.message);
            }
            setHasPendingUploads(true);
            setUploadedFiles((prev) =>
              prev.map((existing) =>
                existing.id === uploadedFile.id
                  ? { ...existing, status: 'completed', progress: 100 }
                  : existing,
              ),
            );
          } catch (uploadFailure) {
            const failureMessage =
              uploadFailure instanceof Error
                ? uploadFailure.message
                : 'Failed to upload one or more files';
            setUploadError(failureMessage);
            setUploadedFiles((prev) =>
              prev.map((existing) =>
                existing.id === uploadedFile.id
                  ? {
                    ...existing,
                    status: 'error',
                    error: failureMessage,
                  }
                  : existing,
              ),
            );
          }
        }),
      );
      if (statusMessages.length > 0) {
        setUploadStatus(statusMessages[statusMessages.length - 1]);
      }
      await loadDocuments();
    },
    [loadDocuments, uploadLineOfBusiness],
  );

  const handleFileRemoved = useCallback((fileId: string): void => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const handleReindex = useCallback(async (): Promise<void> => {
    setReindexing(true);
    setUploadError(null);
    try {
      const result = await KnowledgeBaseService.triggerReindex();
      if (!result.started) {
        setUploadError(result.message);
        setReindexing(false);
        return;
      }
      setUploadStatus(result.message);

      // Keep the button disabled until the indexer finishes, polling its
      // status. A safety cap prevents getting stuck disabled indefinitely.
      for (let attempt = 0; attempt < INDEXER_POLL_MAX_ATTEMPTS; attempt += 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, INDEXER_POLL_INTERVAL_MS),
        );
        let status: string;
        try {
          const statusResult = await KnowledgeBaseService.getReindexStatus();
          status = statusResult.status;
        } catch {
          // Transient status read failure: stop waiting rather than hang.
          break;
        }
        if (status !== INDEXER_STATUS_RUNNING) {
          break;
        }
      }

      // Indexing finished (or we stopped waiting): refresh the list and clear
      // the pending flag so the button greys out until new uploads arrive.
      setHasPendingUploads(false);
      await loadDocuments();
    } catch (reindexFailure) {
      setUploadError(
        reindexFailure instanceof Error
          ? reindexFailure.message
          : 'Failed to start indexing',
      );
    } finally {
      setReindexing(false);
    }
  }, [loadDocuments]);

  const toggleSelection = useCallback((doc: KnowledgeBaseDocument): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(doc.id)) {
        next.delete(doc.id);
      } else {
        next.add(doc.id);
      }
      // On the standalone page there is no explicit "Confirm" button, so
      // persist the selection immediately (to the user's backend profile and
      // the local cache) to keep it in sync with the welcome-flow selection.
      if (!selectionMode) {
        void savePreselectedKnowledgeBaseDocuments(
          documents.filter((document) => next.has(document.id)),
        );
      }
      return next;
    });
  }, [documents, selectionMode]);

  const handleConfirm = useCallback((): void => {
    const selected = documents.filter((doc) => selectedIds.has(doc.id));
    void savePreselectedKnowledgeBaseDocuments(selected);
    if (onConfirm) {
      onConfirm(selected);
      return;
    }
    navigate('/', { replace: true });
  }, [documents, navigate, onConfirm, selectedIds]);

  const handleSkip = useCallback((): void => {
    void savePreselectedKnowledgeBaseDocuments([]);
    if (onConfirm) {
      onConfirm([]);
      return;
    }
    navigate('/', { replace: true });
  }, [navigate, onConfirm]);

  const searchInput = (
    <div className="relative w-full sm:w-72">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={SEARCH_PLACEHOLDER}
        className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
        aria-label={SEARCH_PLACEHOLDER}
      />
    </div>
  );

  const lineOfBusinessFilterChips = (
    <div className="flex flex-wrap gap-2">
      {lineOfBusinessFilterOptions.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => {
            if (option === LOB_FILTER_ALL) {
              setLobFilters(new Set());
              return;
            }
            setLobFilters((previous) => {
              const next = new Set(previous);
              if (next.has(option)) {
                next.delete(option);
              } else {
                next.add(option);
              }
              return next;
            });
          }}
          className={`rounded-full border px-3 py-1 text-xs font-medium ${
            (option === LOB_FILTER_ALL && lobFilters.size === 0) ||
            lobFilters.has(option)
              ? 'border-red-500 bg-red-50 text-red-700'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );

  const renderSelectedGuidelines = (
    variant: SelectedVariant,
  ): React.ReactElement => {
    const styles = SELECTED_STYLES[variant];
    return (
      <>
        <h2 className={styles.heading}>
          <CheckCircleIcon className={styles.headingIcon} />
          {SELECTED_SECTION_TITLE}
          <span className={styles.badge}>{selectedDocuments.length}</span>
        </h2>
        {selectedDocuments.length === 0 ? (
          <p className={styles.empty}>{SELECTED_SECTION_EMPTY}</p>
        ) : (
          <ul className={styles.list}>
            {selectedDocuments.map((doc) => (
              <li key={doc.id} className={styles.row}>
                <DocumentTextIcon className={styles.rowIcon} />
                <span className={styles.name}>{doc.name}</span>
                <button
                  type="button"
                  onClick={() => toggleSelection(doc)}
                  className={styles.remove}
                  aria-label={`Remove ${doc.name} from selection`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  };

  const documentListContent = (
    <>
      {loading && (
        <div className="mt-6 flex justify-center py-8">
          <LoadingSpinner label="Loading knowledge base..." size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          {error}
          <button
            type="button"
            onClick={() => void loadDocuments()}
            className="ml-3 inline-flex items-center rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && filteredDocuments.length === 0 && (
        <p className="mt-6 text-sm text-gray-500">{EMPTY_STATE}</p>
      )}

      {!loading && !error && filteredDocuments.length > 0 && (
        <ul className="mt-4 max-h-80 divide-y divide-gray-100 overflow-y-auto pr-1">
          {filteredDocuments.map((doc) => {
            const isSelected = selectedIds.has(doc.id);
            const readableDescription = humanizeDescription(doc.description);
            return (
              <li
                key={doc.id}
                className="flex items-start gap-3 py-3 px-2 -mx-2 rounded hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  checked={isSelected}
                  onChange={() => toggleSelection(doc)}
                  aria-label={`Select ${doc.name}`}
                />
                <button
                  type="button"
                  onClick={() => toggleSelection(doc)}
                  className="flex flex-1 items-start gap-3 text-left cursor-pointer"
                >
                  <DocumentTextIcon className="mt-1 h-5 w-5 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {doc.name}
                      </p>
                      {doc.sector && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {doc.sector}
                        </span>
                      )}
                      {isSelected && (
                        <CheckCircleIcon className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    {readableDescription && (
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                        {readableDescription}
                      </p>
                    )}
                    {doc.keywords && doc.keywords.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {doc.keywords.slice(0, 5).map((keyword) => (
                          <span
                            key={`${doc.id}-${keyword}`}
                            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );

  if (selectionMode) {
    return (
      <div className="line-of-business-welcome">
        <div className="line-of-business-welcome__stage">
          <div className="line-of-business-welcome__card knowledge-base-welcome__card flex flex-col">
            <div
              className="line-of-business-welcome__hero"
              style={{ backgroundImage: `url(${headerBg})` }}
            >
              <div className="line-of-business-welcome__brand">
                <img src={logo} alt="Shell Logo" className="h-10 w-10" />
                <div>
                  <span className="line-of-business-welcome__brandTitle">
                    {HERO_TITLE}
                  </span>
                </div>
              </div>
            </div>

            <div className="line-of-business-welcome__body knowledge-base-welcome__body flex-1">
              <div className="line-of-business-welcome__intro flex-shrink-0">
                <h1 className="line-of-business-welcome__title">{PAGE_TITLE}</h1>
                <p className="line-of-business-welcome__description">
                  {SELECTION_DESCRIPTION}
                </p>
              </div>

              <div className="knowledge-base-welcome__selected mb-2 rounded-lg border border-gray-200 bg-white p-3">
                {renderSelectedGuidelines('welcome')}
              </div>

              <div className="flex flex-shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  Available Guideline Documents
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {filteredDocuments.length}
                  </span>
                </span>
                {searchInput}
              </div>

              <div className="mt-3 flex-shrink-0">{lineOfBusinessFilterChips}</div>

              <div className="knowledge-base-welcome__list flex min-h-0 flex-col overflow-y-auto">
                {documentListContent}
              </div>
            </div>

            <div className="knowledge-base-welcome__footer">
              {onBack ? (
                <button
                  type="button"
                  className="knowledge-base-welcome__button"
                  onClick={onBack}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  {BACK_LABEL}
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  className="knowledge-base-welcome__button"
                  onClick={handleSkip}
                >
                  {SKIP_LABEL}
                </button>
                <button
                  type="button"
                  className="knowledge-base-welcome__button"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {CONTINUE_LABEL}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-base-100">
      <div className="mx-auto flex w-full max-w-5xl flex-col px-6 py-8">
        <div className="mb-6 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#008557]">
            <DocumentTextIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{PAGE_TITLE}</h1>
            <p className="mt-1 text-base text-gray-600">{PAGE_DESCRIPTION}</p>
          </div>
        </div>

        <section className="flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          {renderSelectedGuidelines('page')}
        </section>

        <section className="mt-8 flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <DocumentTextIcon className="h-5 w-5 text-red-600" />
              Available Guideline Documents
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                {filteredDocuments.length}
              </span>
              <button
                type="button"
                onClick={() => void loadDocuments()}
                disabled={loading}
                className="ml-1 inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                title="Refresh the document list"
              >
                <ArrowPathIcon
                  className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            </h2>
            {searchInput}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {lineOfBusinessFilterChips}
          </div>

          {documentListContent}
        </section>

        <section className="mt-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <CloudArrowUpIcon className="h-5 w-5 text-red-600" />
                {UPLOAD_LABEL}
              </h2>
              <p className="mt-1 text-sm text-gray-600">{UPLOAD_DESCRIPTION}</p>
            </div>
            <button
              type="button"
              onClick={() => void handleReindex()}
              disabled={!hasPendingUploads || reindexing}
              className="knowledge-base__index-button"
              title={
                hasPendingUploads
                  ? 'Index the documents you uploaded so they become searchable'
                  : 'Upload new documents first, then click to index them'
              }
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${reindexing ? 'animate-spin' : ''}`}
              />
              {reindexing ? INDEXING_LABEL : UPLOAD_INDEX_LABEL}
            </button>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">
              <span>
                {UPLOAD_LOB_LABEL}
                <span className="ml-0.5 text-red-600" aria-hidden="true">
                  *
                </span>
              </span>
              <select
                required
                value={uploadLineOfBusiness}
                onChange={(event) =>
                  setUploadLineOfBusiness(event.target.value)
                }
                className={`mt-1 block w-full max-w-md rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-500 sm:w-72 ${
                  uploadLineOfBusiness
                    ? 'border-gray-300 text-gray-800 focus:border-red-500'
                    : 'border-red-300 text-gray-500 focus:border-red-500'
                }`}
              >
                <option value={LOB_UNSELECTED} disabled>
                  {LOB_PLACEHOLDER_LABEL}
                </option>
                {lineOfBusinessOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-4">
            <FileUploader
              onFilesUploaded={(files) => void handleFilesUploaded(files)}
              onFileRemoved={handleFileRemoved}
              uploadedFiles={uploadedFiles}
              disabled={!uploadLineOfBusiness}
              label="Knowledge base documents"
              description={
                uploadLineOfBusiness
                  ? 'Drag and drop files, or click to browse'
                  : LOB_REQUIRED_MESSAGE
              }
            />
          </div>
          {uploadError && (
            <div
              className="mt-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              <span>{uploadError}</span>
            </div>
          )}
          {!uploadError && uploadStatus && (
            <div
              className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700"
              role="status"
            >
              <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
              <span>{uploadStatus}</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default KnowledgeBasePage;
